import { defineComponent as _defineComponent } from 'vue'
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCircleDashed, faRoadCircleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { computed, ref, watch } from 'vue';

import i18n from '@/initI18n';

import ErrorUserMessage from '@/shared/components/ErrorUserMessage.vue';
import Button from '@/shared/components/buttons/Button.vue';
import ButtonLink from '@/shared/components/buttons/ButtonLink.vue';
import isUnique from '@/shared/modules/isUniqueFilter';
import notNullOrUndefined from '@/shared/modules/notNullOrUndefinedFilter';


export default /*#__PURE__*/_defineComponent({
  __name: 'FileUploadTile',
  props: {
    successfulFiles: { default: () => [] },
    failedFiles: { default: () => [] },
    hint: { default: '' },
    required: { type: Boolean, default: false },
    accept: { default: () => [] },
    actionLabel: { default: 'Submit' },
    progress: null,
    validators: { default: () => [] },
    preselect: { type: Boolean, default: false }
  },
  emits: ["click:fileSelection", "update:fileSelection", "reset:fileSelection", "action", "abort"],
  setup(__props: any, { emit }: { emit: ({
  // file that was clicked in selection list
  (e: 'click:fileSelection', file: File): File;
  // emits selected files from last selection event
  (e: 'update:fileSelection', files: File[]): File[];
  (e: 'reset:fileSelection', files: File[]): File[];
  // emits all files inside the tile
  (e: 'action', files: File[]): File[];
  (e: 'abort'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { successfulFiles: File[], failedFiles: File[], hint: string, required: boolean, accept: string[], actionLabel: string, progress?: number, validators: ((files: File[]) => string)[], preselect: boolean }

library.add(faCircleDashed, faRoadCircleExclamation);



const buttonDisabled = ref(false);
const validators = ref<typeof props.validators>([fileFormatValidator, ...props.validators]);


const $t = i18n.translate;

const labelUploadInfo = $t('oder klicke Datei auswählen oder Ordner auswählen, um eine Datei hinzuzufügen.', ['#####']);
const labelUploadInfoParts = labelUploadInfo?.split('#####') ?? [];
const selectedFiles = ref<File[]>([]);
const unsupportedFiles = ref<File[]>([]);
const clickedFiles = ref<File[]>([]);

/* -- Reactive -- */
const fileInput = ref<HTMLInputElement>();
const directoryInput = ref<HTMLInputElement>();
const isDragover = ref(false);
const errorMessages = ref<string[]>([]);

const successfulFileNames = computed<string[]>(() => props.successfulFiles.map((file) => file.name));

watch(
  () => props.successfulFiles,
  (successfulFiles) => {
    const isOnceWatcher = selectedFiles.value.length === 0; // because onceWatchers exist only in Vue 3.4

    if (props.preselect && isOnceWatcher) {
      selectedFiles.value = [...successfulFiles];
    }
  },
);

function updateSelection(event: Event): void {
  const files = Array.from((event.target as HTMLInputElement).files ?? []);
  emitSelection(files);
}

function updateClickedFiles(file: File) {
  clickedFiles.value = clickedFiles.value.includes(file)
    ? clickedFiles.value.filter((f) => f !== file)
    : [...clickedFiles.value, file];
  emit('click:fileSelection', file);
}

/* -- Methods -- */

function actionPressed() {
  buttonDisabled.value = true;
  emit('action', selectedFiles.value);
}

function abortPressed() {
  buttonDisabled.value = false;
  emit('abort');
}

function resetSelection() {
  emit('reset:fileSelection', selectedFiles.value);
  selectedFiles.value = [];
  errorMessages.value = [];
}

function isFileFormatAllowed(file: File): boolean {
  const type = inferFormat(file);
  return props.accept.includes(type);
}

async function onFileDrop(event: DragEvent): Promise<void> {
  if (!event.dataTransfer) {
    return;
  }

  isDragover.value = false;

  const promises = Array.from<DataTransferItem>(event.dataTransfer.items)
    .map((item) => item.webkitGetAsEntry())
    .filter(notNullOrUndefined)
    .map(scan);
  const files = (await Promise.all(promises)).flat();
  emitSelection(files);
}

function emitSelection(files: File[]): void {
  errorMessages.value = validators.value.map((validator) => validator(files)).filter((message) => message.length);

  const supported = files.filter(isFileFormatAllowed);
  const unsupported = files.filter((file) => !isFileFormatAllowed(file));

  selectedFiles.value = [...selectedFiles.value, ...supported];
  unsupportedFiles.value = unsupported;

  emit('update:fileSelection', files);
}

/* eslint-disable no-undef */
async function scan(item: FileSystemEntry): Promise<File[]> {
  if (item.isFile) {
    return new Promise<File[]>((resolve) => {
      /* @ts-ignore no-undef */
      (item as FileSystemFileEntry).file((file) => {
        resolve([file]);
      });
    });
  }
  return scanDirectory(item as FileSystemDirectoryEntry);
}

async function scanDirectory(item: FileSystemDirectoryEntry): Promise<File[]> {
  return new Promise<File[]>((resolve) => {
    item.createReader().readEntries((entries) => {
      const scans = entries.map(scan);
      Promise.all(scans)
        .then((files) => files.flat())
        .then(resolve);
    });
  });
}

/**
 * Infers the file type from file extension, if not auto-detected by file browser - and translates to standard types
 * @param file
 */
function inferFormat(file: File): string {
  if (file.type.length) {
    return file.type;
  }

  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  switch (extension) {
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'zip':
      return 'application/zip';
    case 'xml':
      return 'application/xml';
    case '.csv':
      return 'text/csv';
    case 'isoxml':
      return '.isoxml';
    default:
      return extension;
  }
}

function fileFormatValidator(files: File[]): string {
  const fileFormatIsAllowed = files.every(isFileFormatAllowed);

  if (fileFormatIsAllowed) {
    return '';
  }

  const unsupportedFileFormats = files
    .filter((f) => !isFileFormatAllowed(f))
    .map(inferFormat)
    .filter(isUnique)
    .join(', ');
  return `${unsupportedFileFormats} ${$t('Dateien werden für den Maßnahmenimport nicht unterstützt.')}` ?? '';
}

return { __sfc: true,props, buttonDisabled, validators, emit, $t, labelUploadInfo, labelUploadInfoParts, selectedFiles, unsupportedFiles, clickedFiles, fileInput, directoryInput, isDragover, errorMessages, successfulFileNames, updateSelection, updateClickedFiles, actionPressed, abortPressed, resetSelection, isFileFormatAllowed, onFileDrop, emitSelection, scan, scanDirectory, inferFormat, fileFormatValidator, ErrorUserMessage, Button, ButtonLink }
}

})